import { memo, useCallback } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { TagMultiSelectField } from "../../../TagMultiSelectField";
import { PaymentsField } from "../../PaymentsField";
import type { RentalCreateFormSchema } from "../../rental-create-schema";
import { StepHeading } from "./StepHeading";

interface StepPaymentsProps {
  form: UseFormReturn<RentalCreateFormSchema>;
}

export const StepPayments = memo(function StepPayments({
  form,
}: StepPaymentsProps) {
  const payments = useWatch({ control: form.control, name: "payments" });
  const tagIds = useWatch({ control: form.control, name: "tagIds" });
  const emitInvoice = useWatch({ control: form.control, name: "emitInvoice" });

  const handlePaymentsChange = useCallback(
    (next: typeof payments) =>
      form.setValue("payments", next, { shouldDirty: true }),
    [form],
  );

  const handleTagsChange = useCallback(
    (ids: number[]) => form.setValue("tagIds", ids, { shouldDirty: true }),
    [form],
  );

  return (
    <div className="space-y-6">
      <StepHeading
        title="Valores e pagamentos"
        description="Informe o valor total e registre os pagamentos recebidos."
      />

      <FormField
        control={form.control}
        name="totalValue"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Valor total (R$) *</FormLabel>
            <FormControl>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={field.value || ""}
                placeholder="0,00"
                onChange={(e) => field.onChange(Number(e.target.value) || 0)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <PaymentsField payments={payments} onChange={handlePaymentsChange} />

      <div className="space-y-2">
        <Label htmlFor="rental-create-tags">Tags</Label>
        <TagMultiSelectField
          inputId="rental-create-tags"
          value={tagIds}
          onChange={handleTagsChange}
        />
      </div>

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Observações</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Detalhes do aluguel"
                rows={3}
                maxLength={200}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="emitInvoice"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    // Ao desmarcar, descarta a observação digitada para não
                    // persisti-la numa nota que nunca será emitida.
                    if (!e.target.checked) {
                      form.setValue("invoiceObservation", "");
                    }
                  }}
                />
                <span>
                  Emitir nota fiscal
                  <span className="block text-muted-foreground">
                    Marcado por padrão. Desmarque para não emitir a nota fiscal
                    deste aluguel.
                  </span>
                </span>
              </label>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {emitInvoice && (
        <FormField
          control={form.control}
          name="invoiceObservation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Observação da NF</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Descrição que aparece na nota fiscal"
                  rows={3}
                  maxLength={2000}
                  {...field}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                Opcional. Em branco usa o padrão da loja.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
});
